import { Head } from 'components/Head';
import { MainLayout } from 'components/Layout';
import { Link } from 'react-router-dom';

export const Landing = () => {
  return (
    <MainLayout
      title="Fundo | Agriculture, Reimagined"
      description="Fundo empowers farmers with AI, finance, and market access — even without smartphones."
    >
      {/* HERO */}
      <section className="grid min-h-[80vh] items-center gap-8 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8b5e3c]">
            Fundo Farmer
          </p>

          <h1 className="max-w-[12ch] font-serif text-5xl leading-[0.92] tracking-[-0.04em] text-[#1b140f] sm:text-7xl">
            Agriculture,
            <span className="block text-[#8b5e3c]">intelligently connected.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-8 text-[#4f4034] sm:text-lg">
            This is where agriculture becomes participatory. Run your farm, invest in one, or trade
            in live markets and auctions with intelligence and financial tools built in, accessible
            from smartphones to basic phones.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-[1.5rem] border border-[#1b140f]/10 bg-white/55 px-5 py-4 backdrop-blur-md">
              <div className="text-2xl font-semibold text-[#1b140f]">Virtual</div>
              <div className="text-sm text-[#4f4034]">farm, invest, and earn digitally</div>
            </div>
            <div className="rounded-[1.5rem] border border-[#1b140f]/10 bg-white/55 px-5 py-4 backdrop-blur-md">
              <div className="text-2xl font-semibold text-[#1b140f]">AI</div>
              <div className="text-sm text-[#4f4034]">data-driven farming insights</div>
            </div>
            <div className="rounded-[1.5rem] border border-[#1b140f]/10 bg-white/55 px-5 py-4 backdrop-blur-md">
              <div className="text-2xl font-semibold text-[#1b140f]">USSD</div>
              <div className="text-sm text-[#4f4034]">works without smartphones</div>
            </div>
            <div className="rounded-[1.5rem] border border-[#1b140f]/10 bg-white/55 px-5 py-4 backdrop-blur-md">
              <div className="text-2xl font-semibold text-[#1b140f]">Market</div>
              <div className="text-sm text-[#4f4034]">direct buyer connections</div>
            </div>
          </div>

          <div className="mt-10 flex gap-4">
            <a
            href="https://wa.me/256782692271?text=Hello%2C%20I%20would%20like%20to%20join%20the%20Fundo%20internal%20testing%20group.%0A%0AHere%27s%20my%20email%20address%3A%0ADevice%20I%27m%20using%3A"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-[#1b140f] px-6 py-3 text-sm text-white hover:bg-[#2a1f18] transition"
          >
            Join Fundo's Beta Testing
          </a>
          </div>
        </div>

        <div className="relative min-h-[520px] overflow-hidden rounded-[2rem] border border-[#1b140f]/10 bg-[#1e1915] shadow-[0_30px_80px_rgba(27,20,15,0.18)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,_rgba(220,150,91,0.35),_transparent_24%),radial-gradient(circle_at_80%_70%,_rgba(111,168,150,0.26),_transparent_22%),linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0))]" />
          <div className="absolute left-8 top-8 max-w-xs rounded-[1.75rem] border border-white/10 bg-white/10 p-6 text-[#f7efe5] backdrop-blur-md">
            <p className="text-xs uppercase tracking-[0.25em] text-[#d8b089]">Insight</p>
            <h2 className="mt-3 font-serif text-3xl leading-tight">
              Know your farm like never before.
            </h2>
          </div>

          <div className="absolute bottom-8 right-8 max-w-sm rounded-[1.75rem] border border-white/10 bg-[#f3e7d6] p-6 text-[#1b140f] shadow-xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#8b5e3c]">Access</p>
            <h2 className="mt-3 font-serif text-3xl leading-tight">
              From smartphones to USSD, agriculture for everyone.
            </h2>
          </div>

          <div className="absolute inset-x-10 bottom-24 h-px bg-white/10" />
          <div className="absolute right-16 top-16 h-56 w-56 rounded-full border border-white/10" />
          <div className="absolute right-24 top-24 h-40 w-40 rounded-full border border-white/10" />
        </div>
      </section>

      {/* PROBLEM → SOLUTION */}
      <section className="mt-32 grid gap-12 lg:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">The Problem</p>
          <h2 className="mt-4 font-serif text-4xl text-[#1b140f]">
            Farmers operate without visibility.
          </h2>
          <p className="mt-6 text-[#4f4034] leading-8">
            Most farmers lack access to structured data, reliable markets, and financial tools.
            Decisions are made blindly, productivity stays low, and incomes remain unstable.
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">The Solution</p>
          <h2 className="mt-4 font-serif text-4xl text-[#1b140f]">
            Fundo becomes the operating system of the farm.
          </h2>
          <p className="mt-6 text-[#4f4034] leading-8">
            By combining AI, financial services, and market access into one ecosystem, Fundo
            transforms how farmers plan, grow, and sell.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mt-32">
        <div className="max-w-xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8b5e3c]">Capabilities</p>
          <h2 className="mt-4 font-serif text-4xl text-[#1b140f]">
            Everything a farmer needs. In one system.
          </h2>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              title: 'Farm Intelligence',
              desc: 'Track expenses, yields, and performance with AI-powered insights.',
            },
            {
              title: 'Marketplace',
              desc: 'Sell directly to buyers with transparent pricing.',
            },
            {
              title: 'Financial Tools',
              desc: 'Access loans, savings, and insurance based on real farm data.',
            },
            {
              title: 'Offline Access',
              desc: 'Use Fundo via SMS and USSD without internet.',
            },
            {
              title: 'Smart Reports',
              desc: 'Generate insights automatically from your farm activity.',
            },
            {
              title: 'Scalable Growth',
              desc: 'From smallholder to commercial farming — grow with data.',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-[1.75rem] border border-[#1b140f]/10 bg-white/60 p-6 backdrop-blur-md"
            >
              <h3 className="font-semibold text-[#1b140f]">{item.title}</h3>
              <p className="mt-3 text-sm text-[#4f4034] leading-7">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IMPACT */}
      <section className="mt-32 rounded-[2rem] border border-[#1b140f]/10 bg-[#1b140f] p-12 text-white">
        <div className="grid gap-10 lg:grid-cols-3">
          <div>
            <h3 className="text-4xl font-serif">2x</h3>
            <p className="mt-2 text-sm text-white/70">increase in productivity</p>
          </div>
          <div>
            <h3 className="text-4xl font-serif">+Income</h3>
            <p className="mt-2 text-sm text-white/70">better financial outcomes</p>
          </div>
          <div>
            <h3 className="text-4xl font-serif">Inclusive</h3>
            <p className="mt-2 text-sm text-white/70">accessible to all farmers</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mt-32 text-center">
        <h2 className="font-serif text-4xl text-[#1b140f]">
          Build the future of agriculture with Fundo.
        </h2>
        <p className="mt-6 text-[#4f4034]">
          Whether you're a farmer, investor, or partner — Fundo is your entry point into the next
          generation of agriculture.
        </p>

        <div className="mt-8 flex justify-center gap-4">
          <a
            href="https://wa.me/256782692271?text=Hello%2C%20I%20would%20like%20to%20join%20the%20Fundo%20internal%20testing%20group.%0A%0AHere%27s%20my%20email%20address%3A%0ADevice%20I%27m%20using%3A"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-[#1b140f] px-6 py-3 text-sm text-white hover:bg-[#2a1f18] transition"
          >
            Join Fundo's Beta Testing
          </a>
        </div>
      </section>
    </MainLayout>
  );
};
